/* Formatting + shared enumerations for the console UI. */

export const money = (value, opts = {}) => {
  const n = Number(value || 0);
  const { decimals = 0, sign = false } = opts;
  const formatted = `$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
  if (n < 0) return `-${formatted}`;
  return sign && n > 0 ? `+${formatted}` : formatted;
};

export const moneyCents = (value) => money(value, { decimals: 2 });

export const compactMoney = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `$${Math.round(n / 1000)}K`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return money(n);
};

export const num = (value, decimals = 0) =>
  Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export const compactNum = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000)}K`;
  return num(n);
};

export const pct = (value, decimals = 0) => `${Number(value || 0).toFixed(decimals)}%`;

export const hours = (value) => {
  const n = Number(value || 0);
  if (!n) return '—';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

export const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const dateLabel = (value, opts = {}) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(opts.year ? { year: 'numeric' } : {}),
  });
};

export const timeLabel = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

export const dateTimeLabel = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return `${dateLabel(d)} · ${timeLabel(d)}`;
};

export const weekdayLabel = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '—';
};

export const dayKey = (value) => {
  const d = parseDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const todayKey = () => dayKey(new Date());

export const relative = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const suffix = (text) => (diff >= 0 ? `${text} ago` : `in ${text}`);
  if (abs < 45_000) return 'just now';
  if (abs < hour) return suffix(`${Math.round(abs / minute)}m`);
  if (abs < day) return suffix(`${Math.round(abs / hour)}h`);
  if (abs < 30 * day) return suffix(`${Math.round(abs / day)}d`);
  return dateLabel(d, { year: true });
};

/** "in 3h" / "2h late" for schedule context. */
export const untilLabel = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const hour = 3_600_000;
  const day = 24 * hour;
  if (abs < hour) return diff >= 0 ? `in ${Math.round(abs / 60_000)}m` : `${Math.round(abs / 60_000)}m ago`;
  if (abs < day) return diff >= 0 ? `in ${Math.round(abs / hour)}h` : `${Math.round(abs / hour)}h ago`;
  return diff >= 0 ? `in ${Math.round(abs / day)}d` : `${Math.round(abs / day)}d ago`;
};

export const JOB_STATUS = {
  draft: { label: 'Draft', tone: 'muted' },
  posted: { label: 'Posted', tone: 'amber' },
  assigned: { label: 'Assigned', tone: 'blue' },
  en_route: { label: 'En route', tone: 'violet' },
  checked_in: { label: 'Checked in', tone: 'violet' },
  in_progress: { label: 'In progress', tone: 'lime' },
  completed: { label: 'Completed', tone: 'lime' },
  verified: { label: 'Verified', tone: 'teal' },
  paid: { label: 'Paid', tone: 'green' },
  invoiced: { label: 'Invoiced', tone: 'green' },
  cancelled: { label: 'Cancelled', tone: 'red' },
};

export const JOB_STATUS_ORDER = Object.keys(JOB_STATUS);

export const LUMPER_STATUS = {
  applied: { label: 'Applied', tone: 'amber' },
  screening: { label: 'Screening', tone: 'blue' },
  approved: { label: 'Approved', tone: 'teal' },
  active: { label: 'Active', tone: 'green' },
  paused: { label: 'Paused', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'red' },
  rejected: { label: 'Rejected', tone: 'red' },
};

export const NEXT_ACTION = {
  draft: { to: 'posted', label: 'Post to board', tone: 'amber' },
  posted: { to: 'assigned', label: 'Assign lumper', tone: 'blue' },
  assigned: { to: 'en_route', label: 'Mark en route', tone: 'violet' },
  en_route: { to: 'checked_in', label: 'Check in', tone: 'violet' },
  checked_in: { to: 'in_progress', label: 'Start unload', tone: 'lime' },
  in_progress: { to: 'completed', label: 'Complete job', tone: 'lime' },
  completed: { to: 'verified', label: 'Verify counts', tone: 'teal' },
  verified: { to: 'paid', label: 'Mark paid', tone: 'green' },
  paid: { to: 'invoiced', label: 'Mark invoiced', tone: 'green' },
};

export const FREIGHT_LABEL = {
  floor_loaded: 'Floor loaded',
  pallets: 'Pallets',
  mixed_pallets: 'Mixed pallets',
  produce: 'Produce',
  cases: 'Loose cases',
  cross_dock: 'Cross dock',
  returns: 'Returns',
  live_load: 'Live load',
};

export const WORK_LABEL = {
  unload: 'Unload',
  load: 'Load out',
  sort: 'Sort',
  cross_dock: 'Cross dock',
  putaway: 'Putaway',
  rework: 'Rework',
};

export const titleCase = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const statusMeta = (map, key) => map[key] || { label: titleCase(key), tone: 'muted' };

export const toneFor = (value, thresholds, tones) => {
  for (let i = 0; i < thresholds.length; i += 1) if (value <= thresholds[i]) return tones[i];
  return tones[tones.length - 1];
};

/** Ageing buckets → tone for the AR table. */
export const BUCKET_TONE = {
  current: 'green',
  paid: 'muted',
  '1-30': 'teal',
  '31-60': 'amber',
  '61-90': 'red',
  '90+': 'red',
};

/* --------------------------------------------------------------- phones */

/**
 * The ten digits of a US number, or null when it is not one.
 *
 * Anything else is left alone on purpose: a foreign number, an extension, or
 * something somebody typed as "ask for Ray on the dock" is not for a program to
 * tidy up, and guessing would lose information the office needs.
 */
const usDigits = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;
  // Reject anything carrying an extension or a second number.
  if (/(ext|x|,|;|\/|\+.*\+)/i.test(text)) return null;
  const digits = text.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits.length === 10 ? digits : null;
};

/** A phone number the way a person would write it: (305) 300-4185. */
export const phone = (value) => {
  const digits = usDigits(value);
  if (!digits) return String(value ?? '');
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Digits for a tel: link, so the phone dials the number it shows. */
export const phoneDial = (value) => {
  const digits = usDigits(value);
  if (digits) return `+1${digits}`;
  // Something we chose not to tidy up — dial its first real number and leave
  // any extension out of the link.
  const runs = String(value ?? '').match(/\d[\d\s().-]*\d|\d/g) || [];
  const main = (runs.find((run) => run.replace(/\D/g, '').length >= 7) || '').replace(/\D/g, '');
  if (!main) return '';
  if (main.length === 11 && main.startsWith('1')) return `+${main}`;
  return main.length === 10 ? `+1${main}` : `+${main}`;
};

/**
 * Format while somebody is typing, without fighting them.
 *
 * Once they type something that is not part of a ten-digit number — a plus, a
 * letter, an extension — it stops intervening and takes what they wrote.
 */
export const phoneWhileTyping = (value) => {
  const text = String(value ?? '');
  const digits = text.replace(/\D/g, '');
  if (/[^\d\s()\-.]/.test(text) || digits.length > 10) return text;
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
